import React, { useMemo, useState } from "react";
import { SmallText, Spacer, CustomSLButton, FlexRow } from "../../components/core/utility";
import { AssignMaterialVehicleGrid } from "./AssignMaterialVehicleGrid";
import { Breadcrumbs, BreadcrumbItem } from "@vds/breadcrumbs";
import { OrderShippingSummaryAccordion } from "./OrderShippingSummaryAccordion";
import { redirectToPrevPage } from "./utils";
import { Modal, ModalBody } from "@vds/modals";
import { Title } from "@vds/typography";
import { css } from "@emotion/react";
import { Icon } from "@vds/icons";
import { Button } from "@vds/buttons";

const getVehId = (vehicle) => {
  const stableId = vehicle?._stableId;
  const vehicleId = vehicle?.id || vehicle?.vehicle || vehicle?.vin || stableId;
  return vehicleId;
};

const getWorkticketIds = (mapping) =>
  (mapping?.workticketId || mapping?.workTicketId || "")
    .split(",")
    .map((x) => String(x).trim())
    .filter(Boolean);

const makePerWtKey = (vehicle, mapping, wtId) => {
  const vehicleId = getVehId(vehicle);
  const hardwareId = mapping?.hardwareId || "";
  const subscriptionId = mapping?.subscriptionId || "";
  const workTicketId = String(wtId || "");
  return `${vehicleId}|${hardwareId}|${subscriptionId}|${workTicketId}`;
};

const isMappingDisabled = (vehicle, mapping, disabledSet) => {
  const wts = getWorkticketIds(mapping);
  if (!wts.length) return disabledSet.has(makePerWtKey(vehicle, mapping, ""));
  return wts.every((wt) => disabledSet.has(makePerWtKey(vehicle, mapping, wt)));
};

// Update header to accept disabled for Complete button
const ReviewRequestedItemsHeader = ({ onContinue, disabled }) => (
  <div style={{ margin: "1rem 0", width: "100%" }}>
    <div
      style={{
        fontFamily: "Verizon-NHG-eDS",
        fontWeight: 700,
        fontSize: "20px",
        lineHeight: "24px",
        color: "#000",
      }}
    >
      Review Requested Items
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "80px", // increased spacing between message and button
        marginTop: "8px",
      }}
    >
      <div
        style={{
          color: "#cc0000",
          fontFamily: "Verizon-NHG-eDS",
          fontSize: "11px",
          lineHeight: "16px",
          flex: 1,
        }}
      >
        <b>Please review</b> the items you have established for &quot;Return Only After Cancellation&quot; or &quot;Return and Reship&quot; before
        processing. After clicking &quot;Continue&quot;, this will generate a return ticket and you will not be able to cancel the return ticket. For
        reshipment, the line items will be available as soon as sales order has been generated for the return ticket.
      </div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Button size="small" variant="secondary" onClick={onContinue} aria-label="Complete" disabled={disabled}>
          Complete
        </Button>
      </div>
    </div>
  </div>
);

const buildSelectionIndex = (selectedVehicleEntities) => {
  const selectionIndex = new Map();
  if (!selectedVehicleEntities || typeof selectedVehicleEntities !== "object") return selectionIndex;
  Object.entries(selectedVehicleEntities).forEach(([materialKey, vehicleList]) => {
    (vehicleList || []).forEach((vehicle) => {
      const vehicleId = getVehId(vehicle);
      if (!vehicleId) return;
      if (!selectionIndex.has(vehicleId)) selectionIndex.set(vehicleId, new Set());
      selectionIndex.get(vehicleId).add(materialKey);
    });
  });
  return selectionIndex;
};

const mapBundleToGridData = (bundleId, groupsArray, selectionIndex) => {
  const gridGroups = Array.isArray(groupsArray) ? groupsArray : [];
  const vehiclesForGrid = [];
  gridGroups.forEach((group) => {
    (group?.vehicles || []).forEach((vehicle) => {
      const vehicleId = getVehId(vehicle);
      if (!vehicleId) return;
      const selectedKeysForVehicle = selectionIndex.get(vehicleId) || new Set();
      const allMappings = Array.isArray(vehicle.materialMapping) ? vehicle.materialMapping : [];
      const hasSelected = allMappings.some((mapping) => selectedKeysForVehicle.has(`${mapping?.hardwareId || ""}|${mapping?.subscriptionId || ""}`));
      if (!hasSelected) return;
      const normalizedMappings = allMappings.map((mapping) => ({
        ...mapping,
        _isSelected: selectedKeysForVehicle.has(`${mapping?.hardwareId || ""}|${mapping?.subscriptionId || ""}`),
        vinMaterials:
          Array.isArray(mapping?.vinMaterials) && mapping.vinMaterials.length
            ? mapping.vinMaterials
            : [{ materialName: mapping?.materialName || mapping?.hardwareName || mapping?.materialId || "Device" }],
      }));

      vehiclesForGrid.push({
        ...vehicle,
        id: vehicleId,
        materialMapping: normalizedMappings,
      });
    });
  });

  if (!vehiclesForGrid.length) return null;

  return {
    vehicleList: {
      [bundleId]: [
        {
          address: gridGroups?.[0]?.address || {},
          contact: gridGroups?.[0]?.contact || {},
          vehicles: vehiclesForGrid,
        },
      ],
    },
    externalSelectedVehicleIds: vehiclesForGrid.map((v) => v.id),
  };
};

const ReturnReshipSummaryComponent = ({
  combinedVehiclesByBundle = {},
  selectedVehicleEntities = {},
  onContinue,
  onRequestEsns,
  selectedMaterialDetails = [],
}) => {
  const selectionIndex = useMemo(() => buildSelectionIndex(selectedVehicleEntities), [selectedVehicleEntities]);
  const bundleEntries = useMemo(() => Object.entries(combinedVehiclesByBundle || {}), [combinedVehiclesByBundle]);

  const [disabledMap, setDisabledMap] = useState(new Set());
  const [disabledEsnObjects, setDisabledEsnObjects] = useState([]);
  // Track combos where only 1 ESN remains so we can skip modal next time
  const [skipEsnModalCombos, setSkipEsnModalCombos] = useState(new Set());

  // Modal state for ESN confirmation
  const [isEsnModalOpen, setIsEsnModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { vehicle, mapping, materialKey, wtId }
  const [selectedEsn, setSelectedEsn] = useState("");
  const [esnOptions, setEsnOptions] = useState([]);
  // Inline search state inside the dropdown panel and open/close
  const [esnSearchTerm, setEsnSearchTerm] = useState("");
  const [isEsnPickerOpen, setIsEsnPickerOpen] = useState(true);

  // Update openEsnModal to accept wtId as an argument
  const openEsnModal = async (vehicle, mapping, materialKey, wtIdOverride) => {
    const wts = getWorkticketIds(mapping);
    const wtId = wtIdOverride !== undefined ? wtIdOverride : wts[0] || "";
    const comboKey = `${materialKey}|${wtId}`;
    if (skipEsnModalCombos.has(comboKey)) {
      setDisabledMap((prev) => {
        const next = new Set(prev);
        if (wts.length === 0) {
          next.add(makePerWtKey(vehicle, mapping, ""));
        } else {
          wts.forEach((wt) => next.add(makePerWtKey(vehicle, mapping, wt)));
        }
        return next;
      });
      return;
    }

    setPendingDelete({ vehicle, mapping, materialKey, wtId });
    let esns = [];
    if (typeof onRequestEsns === "function") {
      try {
        esns = (await onRequestEsns(materialKey, wtId)) || [];
      } catch (e) {
        esns = [];
      }
    } else if (selectedMaterialDetails && selectedMaterialDetails.length) {
      const [hardwareId, subscriptionId] = materialKey.split("|");
      const matches = selectedMaterialDetails.filter(
        (item) =>
          String(item.hardwareId) === String(hardwareId) &&
          String(item.subscriptionId) === String(subscriptionId) &&
          (!wtId || String(item.workTicketId) === String(wtId))
      );
      esns = matches.flatMap((item) => (Array.isArray(item.esns) ? item.esns : item.esn ? [item.esn] : []));
      esns = Array.from(new Set(esns.filter(Boolean)));
    }

    // Exclude ESNs that were already removed for this hardware/subscription/workticket
    try {
      const [hardwareId, subscriptionId] = materialKey.split("|");
      const disabledSet = new Set(
        (disabledEsnObjects || [])
          .filter(
            (d) =>
              String(d.workticketId || "") === String(wtId) &&
              String(d.hardwareId || "") === String(hardwareId) &&
              String(d.subscriptionId || "") === String(subscriptionId)
          )
          .map((d) => String(d.esn))
      );
      if (disabledSet.size > 0) {
        esns = esns.filter((e) => !disabledSet.has(String(e)));
      }
    } catch (_) {
      // ignore
    }

    if (!esns.length || esns.length === 1) {
      setDisabledMap((prev) => {
        const next = new Set(prev);
        if (wts.length === 0) {
          next.add(makePerWtKey(vehicle, mapping, ""));
        } else {
          wts.forEach((wt) => next.add(makePerWtKey(vehicle, mapping, wt)));
        }
        return next;
      });
      setSkipEsnModalCombos((prev) => {
        const next = new Set(prev);
        next.add(comboKey);
        return next;
      });
      return;
    }
    setEsnOptions(esns);
    setSelectedEsn(esns[0] || "");
    setEsnSearchTerm("");
    setIsEsnPickerOpen(true);
    setIsEsnModalOpen(true);
  };

  const closeEsnModal = () => {
    setIsEsnModalOpen(false);
    setPendingDelete(null);
    setSelectedEsn("");
    setEsnOptions([]);
    setEsnSearchTerm("");
    setIsEsnPickerOpen(false);
  };

  const confirmDeleteSelectedMapping = () => {
    if (!pendingDelete) return closeEsnModal();
    const { vehicle, mapping, materialKey, wtId } = pendingDelete;
    const worktickets = getWorkticketIds(mapping);

    if (selectedEsn && mapping.esnTobeReturned && Array.isArray(mapping.esnTobeReturned)) {
      mapping.esnTobeReturned = mapping.esnTobeReturned.filter(
        (esnObj) => esnObj.esn !== selectedEsn && String(esnObj.esnId) !== String(selectedEsn)
      );
    }

    // Update ESN dropdown options to remove the selected ESN
    setEsnOptions((prev) => (Array.isArray(prev) ? prev.filter((e) => String(e) !== String(selectedEsn)) : prev));

    const remainingCount = Array.isArray(esnOptions) ? esnOptions.filter((e) => String(e) !== String(selectedEsn)).length : 0;
    if (remainingCount === 1) {
      const comboKey = `${materialKey}|${wtId || ""}`;
      setSkipEsnModalCombos((prev) => {
        const next = new Set(prev);
        next.add(comboKey);
        return next;
      });
    }

    const hardwareId = mapping?.hardwareId || "";
    const subscriptionId = mapping?.subscriptionId || "";
    if (worktickets.length === 0) {
      setDisabledEsnObjects((prev) => [...prev, { esn: selectedEsn, hardwareId, subscriptionId, workticketId: "" }]);
    } else {
      setDisabledEsnObjects((prev) => [...prev, ...worktickets.map((wt) => ({ esn: selectedEsn, hardwareId, subscriptionId, workticketId: wt }))]);
    }

    setDisabledMap((prev) => {
      const next = new Set(prev);
      if (worktickets.length === 0) {
        next.add(makePerWtKey(vehicle, mapping, ""));
      } else {
        worktickets.forEach((wt) => next.add(makePerWtKey(vehicle, mapping, wt)));
      }
      return next;
    });
    closeEsnModal();
  };

  const handleDeleteSelectedMapping = (vehicle, mapping) => {
    // Always use the mapping's hardwareId|subscriptionId as materialKey and the correct workticketId
    const materialKey = `${mapping?.hardwareId || ""}|${mapping?.subscriptionId || ""}`;
    // Use the first workticketId if multiple (for modal)
    const wts = getWorkticketIds(mapping);
    const wtId = wts[0] || "";
    openEsnModal(vehicle, mapping, materialKey, wtId);
  };

  const hasAnyHighlighted = useMemo(() => {
    let anyHighlighted = false;
    bundleEntries.forEach(([, groups]) => {
      (groups || []).forEach((group) => {
        (group?.vehicles || []).forEach((vehicle) => {
          const vehicleId = getVehId(vehicle);
          const selectedKeysForVehicle = selectionIndex.get(vehicleId) || new Set();
          (vehicle?.materialMapping || []).forEach((mapping) => {
            if (anyHighlighted) return;
            const initiallySelected = selectedKeysForVehicle.has(`${mapping?.hardwareId || ""}|${mapping?.subscriptionId || ""}`);
            if (!initiallySelected) return;
            if (!isMappingDisabled(vehicle, mapping, disabledMap)) anyHighlighted = true;
          });
        });
      });
    });
    return anyHighlighted;
  }, [bundleEntries, selectionIndex, disabledMap]);

  const dataForBundle = (bundleId, groups) => {
    const base = mapBundleToGridData(bundleId, groups, selectionIndex);
    if (!base) return null;
    const vehicleGroups = base.vehicleList[bundleId] || [];
    const transformedGroups = vehicleGroups.map((group) => {
      const updatedVehicles = (group.vehicles || []).map((vehicle) => {
        const updatedMappings = (vehicle.materialMapping || []).map((mapping) => {
          const disabled = isMappingDisabled(vehicle, mapping, disabledMap);
          return disabled ? { ...mapping, _isSelected: false } : mapping;
        });
        return { ...vehicle, materialMapping: updatedMappings };
      });
      return { ...group, vehicles: updatedVehicles };
    });
    return {
      vehicleList: { [bundleId]: transformedGroups },
      externalSelectedVehicleIds: transformedGroups.flatMap((group) => (group.vehicles || []).map((vehicle) => vehicle.id)),
    };
  };

  const handleComplete = () => {
    if (typeof onContinue === "function") {
      onContinue({
        disabledKeys: Array.from(disabledMap),
        disabledEsnObjects,
      });
    }
  };

  const StyledModal = css`
    max-height: 99vmin;
    padding: 1rem 1rem 1rem 1rem;
    padding-right: 2px;
    border-radius: 0.5rem !important;
    @media (max-width: 767px) {
      width: 560px !important;
      height: auto;
      min-height: 14.5rem;
      padding: 1rem 1rem 1rem 1rem;
      padding-right: 2px;
    }
    & footer {
      padding: 0;
    }
  `;

  const renderEsnConfirmModal = () => {
    if (!isEsnModalOpen || !pendingDelete) return null;
    // Filter options based on inline search term
    const filteredEsns = Array.isArray(esnOptions)
      ? esnOptions.filter((e) => String(e).toLowerCase().includes(String(esnSearchTerm).toLowerCase()))
      : [];
    const enableScroll = filteredEsns.length > 2;
    return (
      <Modal
        role="dialog"
        ariaLabel="Confirmation Modal"
        hideCloseButton={false}
        opened={isEsnModalOpen}
        disableOutsideClick
        css={StyledModal}
        closeButton={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title size="medium">Which ESN do you want to remove?</Title>
            <div
              data-testId="close-icon"
              onClick={() => setIsEsnModalOpen(false)}
              style={{ marginRight: "1rem", marginTop: "0.3rem", cursor: "pointer" }}
            >
              <Icon
                aria-label="close"
                alt="close-icon"
                role="img"
                name="close"
                size="medium"
                color="#000000"
                style={{ cursor: "pointer", outline: "none" }}
              />
            </div>
          </div>
        }
      >
        <ModalBody css={{ paddingLeft: "0", paddingRight: "0", marginTop: "0.5rem" }}>
          <div style={{ fontSize: "16px", marginBottom: "12px", fontFamily: "Verizon-NHG-eTX", color: "#000000" }}>
            To confirm the removal, <b>select the ESN</b> for the line item.
          </div>
          <label style={{ display: "block", fontSize: "16px", marginBottom: "6px", fontFamily: "Verizon-NHG-eTX", color: "#000000" }}>ESN</label>
          <div style={{ position: "relative", fontFamily: "Verizon-NHG-eTX" }}>
            <div
              role="combobox"
              aria-expanded={isEsnPickerOpen}
              onClick={() => setIsEsnPickerOpen((o) => !o)}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #6f7171",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: "none",
                fontSize: "16px",
                color: "#000000",
              }}
            >
              <span style={{ color: selectedEsn ? "#000000" : "#6f7171" }}>{selectedEsn || "Select ESN"}</span>
              <span style={{ fontSize: "12px", color: "#6f7171" }}>{isEsnPickerOpen ? "▲" : "▼"}</span>
            </div>
            {isEsnPickerOpen && (
              <div
                role="listbox"
                style={{
                  position: "relative",
                  marginTop: "6px",
                  border: "1px solid #6f7171",
                  borderRadius: "4px",
                  background: "#fff",
                  boxShadow: "none",
                  padding: "6px",
                  fontFamily: "Verizon-NHG-eTX",
                }}
              >
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    value={esnSearchTerm}
                    onChange={(e) => setEsnSearchTerm(e.target.value)}
                    placeholder="Search ESNs"
                    style={{
                      width: "100%",
                      padding: "10px 30px 10px 28px",
                      border: "1px solid #6f7171",
                      borderRadius: "4px",
                      fontFamily: "Verizon-NHG-eTX",
                      fontSize: "16px",
                      color: "#000000",
                      boxShadow: "none",
                    }}
                  />
                  {/* Left search icon when empty */}
                  {String(esnSearchTerm).length === 0 && (
                    <span style={{ position: "absolute", left: 6, top: 10 }}>
                      <Icon name="search" aria-label="search" role="img" size="small" color="#6f7171" />
                    </span>
                  )}
                  {/* Right clear (cross) icon when user typed something */}
                  {String(esnSearchTerm).length > 0 && (
                    <span
                      style={{ position: "absolute", right: 6, top: 10, cursor: "pointer" }}
                      onClick={() => {
                        setEsnSearchTerm("");
                      }}
                    >
                      <Icon name="close" aria-label="clear" role="img" size="small" color="#6f7171" />
                    </span>
                  )}
                </div>
                <div
                  style={{
                    marginTop: "8px",
                    maxHeight: enableScroll ? "120px" : "unset",
                    overflowY: enableScroll ? "auto" : "hidden",
                    overflowX: "hidden",
                    scrollbarWidth: "thin",
                  }}
                >
                  {filteredEsns.length ? (
                    filteredEsns.map((e) => (
                      <div
                        key={e}
                        role="option"
                        aria-selected={String(selectedEsn) === String(e)}
                        onClick={() => {
                          setSelectedEsn(e);
                          setIsEsnPickerOpen(false);
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: "4px",
                          background: String(selectedEsn) === String(e) ? "#f0f8ff" : "transparent",
                          cursor: "pointer",
                          fontSize: "16px",
                          color: "#000000",
                          fontFamily: "Verizon-NHG-eTX",
                        }}
                      >
                        {e}
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: "6px", color: "#6f7171", fontFamily: "Verizon-NHG-eTX", fontSize: "16px" }}>No ESNs found</div>
                  )}
                </div>
              </div>
            )}
          </div>
          <Spacer top="2rem" />
          <FlexRow style={{ marginLeft: "35%" }}>
            <CustomSLButton aria-label="Cancel" role="button" secondary onClick={closeEsnModal}>
              Cancel
            </CustomSLButton>
            <CustomSLButton
              aria-label="Confirm"
              role="button"
              css={{ padding: "0 10px 0 10px" }}
              primary
              onClick={confirmDeleteSelectedMapping}
              disabled={!selectedEsn}
            >
              Confirm
            </CustomSLButton>
          </FlexRow>
        </ModalBody>
      </Modal>
    );
  };

  const renderBreadcrumbs = () => (
    <>
      <Spacer top="10px" />
      <Breadcrumbs surface="light">
        <BreadcrumbItem key={0} active={false}>
          <span
            css={{ fontWeight: "400", color: "black", cursor: "pointer" }}
            onClick={() => {
              redirectToPrevPage();
            }}
          >
            Return / Reship
          </span>
        </BreadcrumbItem>
        <BreadcrumbItem key={1} active={false}>
          <span css={{ fontWeight: "700", color: "black" }}>Review Requested Items</span>
        </BreadcrumbItem>
      </Breadcrumbs>
      <Spacer bottom="1rem" />
      <OrderShippingSummaryAccordion />
    </>
  );

  return (
    <div>
      {renderBreadcrumbs()}
      {renderEsnConfirmModal()}
      <ReviewRequestedItemsHeader onContinue={handleComplete} disabled={!hasAnyHighlighted} />
      <div style={{ margin: "1rem 0", width: "100%" }}>
        {bundleEntries.length ? (
          bundleEntries.map(([bundleId, groups]) => {
            const data = dataForBundle(bundleId, groups);
            if (!data) return null;
            return (
              <div key={bundleId} style={{ marginBottom: "16px" }}>
                <Spacer top="6px" />
                <AssignMaterialVehicleGrid
                  bundleIds={[bundleId]}
                  addressId={""}
                  contactId={""}
                  crmRefId={""}
                  materialFilter={{}}
                  vehicleList={data.vehicleList}
                  activeBundleId={bundleId}
                  selectedMaterialDetails={null}
                  materialKey={"summary"}
                  externalSelectedVehicleIds={data.externalSelectedVehicleIds}
                  onSelectionChange={() => {}}
                  selectionLocked={true}
                  openAllAccordions={true}
                  selectionHighlight={true}
                  hideSelectionColumn={true}
                  showDeleteForSelected={true}
                  onDeleteSelectedMapping={handleDeleteSelectedMapping}
                />
              </div>
            );
          })
        ) : (
          <SmallText>No selected items</SmallText>
        )}
      </div>
    </div>
  );
};

export default ReturnReshipSummaryComponent;
